import { Box, Typography, Skeleton, useTheme, alpha } from "@mui/material";
import React, { useEffect, useState } from "react";
import { fetchOrderCountByDocType } from "../../services/orderAnalytics.service";
import { format2 } from "../../helpers/helper";


const EMPTY_STATE = {
    year: new Date().getFullYear(),
    newOrders: { count: 0, value: 0 },
    partOrders: { count: 0, value: 0 },
    totalValue: 0,
};
function OrdersPlacedCard({ dateParams, isDateRangeIncomplete }) {
    const theme = useTheme();
    const [data, setData] = useState(EMPTY_STATE);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { from, to } = dateParams || {};

    useEffect(() => {
        if (isDateRangeIncomplete || !from || !to) {
            setData(EMPTY_STATE);
            setIsLoading(false);
            return;
        }

        let isMounted = true;

        const fetchOrdersPlaced = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const list = await fetchOrderCountByDocType({ from, to });
                if (!isMounted) return;

                const newOrder = list.find(i => i.id === "SO") || {};
                const partOrder = list.find(i => i.id === "AO") || {};

                setData({
                    year: from.slice(0, 4),
                    newOrders: {
                        count: newOrder.orderCount || 0,
                        value: newOrder.total || 0,
                    },
                    partOrders: {
                        count: partOrder.orderCount || 0,
                        value: partOrder.total || 0,
                    },
                    totalValue:
                        (newOrder.total || 0) + (partOrder.total || 0),
                });
            } catch (err) {
                if (isMounted) {
                    console.error("OrdersPlacedCard API error", err);
                    setError("Unable to load orders data");
                    setData(EMPTY_STATE);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchOrdersPlaced();
        return () => {
            isMounted = false;
        };
    }, [from, to, isDateRangeIncomplete]);

    const totalCount = data.newOrders.count + data.partOrders.count;
    const newPercent = totalCount ? (data.newOrders.count / totalCount) * 100 : 0;
    const partPercent = totalCount ? (data.partOrders.count / totalCount) * 100 : 0;

    const ShimmerContent = () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Skeleton variant="text" width={80} height={16} />
                <Skeleton variant="text" width={80} height={16} />
            </Box>
            <Skeleton variant="rectangular" width="100%" height={14} sx={{ borderRadius: 999 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: "4px" }}>
                <Skeleton variant="text" width={60} height={16} />
                <Skeleton variant="text" width={60} height={16} />
            </Box>
        </Box>
    );

    if (isLoading) {
        return (
            <Box
                sx={{
                    minHeight: 210,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    p: { xs: 2.5, lg: 2 },
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                }}
            >
                <Typography sx={{ fontSize: { xs: 12, lg: 14 }, color: "secondary.main" }}>
                    Orders Placed – {data.year}
                </Typography>
                <ShimmerContent />
                <Skeleton variant="rectangular" width="100%" height={32} sx={{ borderRadius: 999 }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    minHeight: 210,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: { xs: 2.5, lg: 2 },
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                }}
            >
                <Typography sx={{ color: "error.main", fontSize: 12 }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    if (isDateRangeIncomplete) {
        return (
            <Box
                sx={{
                    minHeight: 210,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: { xs: 2.5, lg: 2 },
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                }}
            >
                <Typography sx={{ color: "warning.main", fontSize: 12 }}>
                    Select both From & To dates
                </Typography>
            </Box>
        );
    }

    if (data.newOrders.count === 0 && data.partOrders.count === 0) {
        return (
            <Box
                sx={{
                    minHeight: 210,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: { xs: 2.5, lg: 2 },
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                }}
            >
                <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                    No order data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: 210,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: { xs: 2.5, lg: 2 },
                borderRadius: "20px",
                bgcolor: "background.paper",
                boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                transition: "all 0.2s ease",
                "&:hover": { transform: "translateY(-2px)" },
            }}
        >
            <Typography sx={{ fontSize: { xs: 12, lg: 14 }, color: "secondary.main" }}>
                Orders Placed – {data.year}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "12px",
                        lineHeight: "12px",
                        color: "text.secondary",
                    }}
                >
                    <span>
                        NEW:{" "}
                        <span style={{ color: "#8695F5", fontWeight: 500 }}>
                            {data.newOrders.count.toLocaleString()}
                        </span>
                    </span>

                    <span>
                        PART:{" "}
                        <span style={{ color: "#37C8C3", fontWeight: 500 }}>
                            {data.partOrders.count.toLocaleString()}
                        </span>
                    </span>
                </Box>

                <Box
                    sx={{
                        position: "relative",
                        height: 14,
                        borderRadius: 999,
                        overflow: "hidden",
                        display: "flex",
                        bgcolor: "background.default",
                    }}
                >
                    <Box
                        sx={{
                            width: `${newPercent}%`,
                            backgroundColor: "#8695F5",
                            borderTopLeftRadius: "999px",
                            borderBottomLeftRadius: "999px",
                        }}
                    />

                    <Box
                        sx={{
                            width: `${partPercent}%`,
                            backgroundColor: "#07B8B2",
                            borderTopRightRadius: "999px",
                            borderBottomRightRadius: "999px",
                        }}
                    />

                    <Box
                        sx={{
                            position: "absolute",
                            left: `${newPercent}%`,
                            top: -4,
                            height: 26,
                            padding: 1,
                            borderLeft: "2px dashed",
                            borderColor: "divider",
                            transform: "translateX(-1px)",
                            pointerEvents: "none",
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: "4px",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "text.primary" }}>
                        <Box sx={{ width: "3px", height: "16px", borderRadius: "2px", backgroundColor: "#8695F5" }} />
                        <span>{format2(data.newOrders.value)}</span>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "text.primary" }}>
                        <span>{format2(data.partOrders.value)}</span>
                        <Box sx={{ width: "3px", height: "16px", borderRadius: "2px", backgroundColor: "#07B8B2" }} />
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    textAlign: "center",
                    fontSize: "12px",
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.8)}`,
                    borderRadius: "999px",
                    py: "6px",
                    color: "secondary.main",
                    boxShadow: `0 0 8px ${alpha(theme.palette.secondary.main, 0.25)}`,
                }}
            >
                Total Value:{" "}
                <span style={{ fontWeight: 600 }}>
                    {format2(data.totalValue)}
                </span>
            </Box>
        </Box>
    );
}

export default React.memo(OrdersPlacedCard);